/**
 * TravelTime answers, kept for a day (contract §17): in memory for this page,
 * and in `localStorage` as `cg.tt.<hash>` so a reload or the next visit draws
 * the same polygon and the same minutes without asking again.
 *
 * A key is the request in words ("map|40.7359,-73.9911|walk|15"); the stored
 * name is its hash, so the keys stay short. Anything older than a day is
 * dropped when read, and every stale entry goes the first time the cache is
 * touched. A full quota drops the oldest entries and tries once more; if the
 * browser won't store at all, memory alone still spares the network.
 */

export const TT_PREFIX = 'cg.tt.';
export const TT_TTL_MS = 24 * 60 * 60 * 1000;

interface Entry<T> {
  /** The request in words, so a hash collision is caught rather than served. */
  k: string;
  /** Saved at, epoch ms. */
  t: number;
  v: T;
}

/** cyrb53: fast, well spread, and 53 bits is plenty for a few hundred keys. */
export function hashKey(key: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < key.length; i++) {
    const ch = key.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

export interface TtCache {
  get<T>(key: string): T | undefined;
  set<T>(key: string, value: T): void;
}

/** What the cache needs of `localStorage`; tests pass a Map-backed one. */
export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

function browserStorage(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function createTtCache(
  storage: KeyValueStore | null = browserStorage(),
  now: () => number = Date.now,
): TtCache {
  const memory = new Map<string, Entry<unknown>>();
  let swept = false;

  /** Every `cg.tt.*` name with its saved-at time; unreadable ones count as oldest. */
  const stored = (): { name: string; t: number }[] => {
    if (!storage) return [];
    const out: { name: string; t: number }[] = [];
    for (let i = 0; i < storage.length; i++) {
      const name = storage.key(i);
      if (!name?.startsWith(TT_PREFIX)) continue;
      let t = 0;
      try {
        t = (JSON.parse(storage.getItem(name) ?? '') as Entry<unknown>).t ?? 0;
      } catch {
        // Unreadable: the first to go.
      }
      out.push({ name, t });
    }
    return out;
  };

  const sweep = () => {
    if (swept || !storage) return;
    swept = true;
    try {
      const cutoff = now() - TT_TTL_MS;
      for (const { name, t } of stored()) if (t < cutoff) storage.removeItem(name);
    } catch {
      // Site data blocked: memory only.
    }
  };

  const fresh = (e: Entry<unknown> | undefined, key: string) => e && e.k === key && now() - e.t < TT_TTL_MS;

  return {
    get<T>(key: string): T | undefined {
      sweep();
      const inMemory = memory.get(key);
      if (fresh(inMemory, key)) return inMemory!.v as T;
      memory.delete(key);
      if (!storage) return undefined;
      try {
        const raw = storage.getItem(TT_PREFIX + hashKey(key));
        if (!raw) return undefined;
        const e = JSON.parse(raw) as Entry<T>;
        if (!fresh(e, key)) return undefined;
        memory.set(key, e);
        return e.v;
      } catch {
        return undefined;
      }
    },
    set<T>(key: string, value: T): void {
      sweep();
      const e: Entry<T> = { k: key, t: now(), v: value };
      memory.set(key, e);
      if (!storage) return;
      const name = TT_PREFIX + hashKey(key);
      const raw = JSON.stringify(e);
      try {
        storage.setItem(name, raw);
      } catch {
        // Full: drop the oldest half of ours and try once more.
        try {
          const all = stored().sort((a, b) => a.t - b.t);
          for (const { name: old } of all.slice(0, Math.max(1, Math.ceil(all.length / 2)))) storage.removeItem(old);
          storage.setItem(name, raw);
        } catch {
          // Still no room: memory will do for this page.
        }
      }
    },
  };
}
